import { z } from "zod";
import {
  nodesAtom,
  edgesAtom,
  chatbotsAppAtom,
  agentsAppAtom,
  interfacesAppAtom,
  tablesAppAtom,
  saveCanvasSetter,
  isAgentsEnabledAtom,
  type FormsTerminology,
  formsTerminologyAtom,
} from "@zapier/canvas-state";
import { FALLBACK_IMPLEMENTATION_IDS } from "@zapier/canvas-constants";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import { mergeModifiedNodeData } from "./mergeNodeData";
import { edgeDiffSchema, nodeDiffSchema, parameters } from "./schema";

function uuid() {
  const id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    },
  );

  return id;
}

function isValidUUID(value: string) {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

const getDescription = ({
  interfacesTerminology,
}: {
  interfacesTerminology: FormsTerminology;
}) => {
  return `Create beautiful, logical workflow visualizations on the canvas. Think of this as designing a visual story that shows how data flows and work gets done.

## WORKFLOW DESIGN PRINCIPLES

**Think Visual Flow**: Design layouts that tell a clear story from left to right:
- START (triggers/inputs) → PROCESS (logic/storage) → OUTPUT (actions/destinations)
- External triggers (3rd party) → Data capture (${interfacesTerminology.pluralLower}) → Data storage (tables) → Processing (zaps/agents) → External actions (3rd party)

**Common Effective Patterns**:
- **Simple Flow**: Gmail → ${interfacesTerminology.singular} → Table → Slack
- **AI-Enhanced**: ${interfacesTerminology.singular} → Table → Agent (analysis) → Zap (routing) → Notifications
- **Multi-Input**: Multiple 3rd party inputs → Centralized table → Branched outputs
- **User Journey**: ${interfacesTerminology.singular} (input) → Chatbot (engagement) → Agent (processing) → Multiple outputs

**Layout Guidelines**:
- Position related nodes near each other vertically
- Use consistent spacing for visual rhythm
- Group similar function types when logical
- Consider the user's mental model of the workflow
- Override positions of nodes if nodes are very poorly organized or way out of place.

## EDGES

- When a node's product, such as a zap,  has a reference to another node's product, such as a table, you must add an edge between the two nodes.
Sample edge:

\`\`\`
  {
    "source": "3ecb7a08-9b5d-436d-89f5-74d58643ab5f",
    "sourceHandle": "right",
    "target": "f0b58dcb-f2e3-4b03-b877-80d98fc3c270",
    "targetHandle": "left",
    "id": "b81a7cce-df53-447c-95f5-1a5bc23aeccb",
    "markerEnd": {
      "type": "arrow",
      "strokeWidth": 1.5,
      "color": "#413735"
    }
  }
\`\`\`
- The goal of an edge is to document the process
- If a node's product references another node's product, the referencer will be the source and the referenced will be the target.
- If an product stops referencing another node's product, you must remove the edge.
- source and target are ids of the nodes. If the id is not a uuid, do not set it. You can call edit_canvas again with the real canvas node ids.

## POSITIONING

**Default Columns** (left to right workflow):
- ${interfacesTerminology.pluralLower}: x=60 (data input)
- tables: x=460 (data storage)
- zaps: x=860 (processing logic)
- chatbots: x=1260 (user interaction)
- agents: x=1660 (AI processing)
- 3rd party: x=2060+ (external integrations, auto-spread across sub-columns)

**Smart Positioning Tips**:
- Override defaults to improve visual flow: { position: { x: 200, y: 300 } }
- Align related nodes horizontally to show parallel processes
- Stagger Y positions to show sequence or priority
- Use whitespace effectively - don't crowd everything together


# Modifying Nodes
- When modifying a node, you must provide the changes to the node in the nextData field.
- !!important!! Provide the asset ids such as tableId, chatbotId, etc. in the data field

#Required:
- Think about the visual story before positioning nodes
- Create layouts that users can quickly understand and follow
`;
};

export const editCanvasAtom = atom<JotaiTool<z.infer<typeof parameters>>>(
  (get) => {
    const interfacesTerminology = get(formsTerminologyAtom);

    return {
      name: "edit_canvas",
      description: getDescription({ interfacesTerminology }),
      parameters,

      execute: atom(null, async (get, set, { args }) => {
        try {
          const isAgentsEnabled = get(isAgentsEnabledAtom);

          // Get current nodes and edges from store
          const currentNodes = get(nodesAtom);
          const currentEdges = get(edgesAtom);

          // Get app atoms for service inference
          const tablesApp = get(tablesAppAtom);
          const interfacesApp = get(interfacesAppAtom);
          const chatbotsApp = get(chatbotsAppAtom);
          const agentsApp = get(agentsAppAtom);

          // Helper function to infer node properties based on type
          const inferNodeProperties = (nodeData: any, existingNodes: any[]) => {
            const inferred = { ...nodeData };
            const nodeType = inferred.assetType;

            // Standard column positions (matching zslOutlineToNodesAndEdgesAtom.ts)
            const columnPositions = {
              interface: 60,
              form: 60,
              table: 460,
              zap: 860,
              chatbot: 1260,
              agent: 1660,
              "3rd_party": 2060, // Add column for 3rd party nodes
              text: 0,
            };

            // Auto-generate position if not provided
            if (!inferred.position) {
              const column =
                columnPositions[
                  inferred.assetType as keyof typeof columnPositions
                ] || 0;

              // For 3rd party nodes, allow multiple columns to spread them out
              let finalColumn = column;
              if (nodeType === "3rd_party") {
                const existing3rdPartyNodes = existingNodes.filter((node) => {
                  return (
                    node.nodeType === "3rd_party" ||
                    node.type === "3rd_party" ||
                    (node.data?.app && !node.data.app.startsWith("Zapier"))
                  );
                });
                // Spread 3rd party nodes across multiple columns (every 400px starting from 2060)
                const columnIndex = existing3rdPartyNodes.length % 3; // Max 3 columns
                finalColumn = 2060 + columnIndex * 400;
              }

              const existingNodesInColumn = existingNodes.filter((node) => {
                const nodeTypeMapped =
                  node.type === "zap"
                    ? "zap"
                    : node.type === "text"
                      ? "text"
                      : node.data?.app === "Zapier Tables"
                        ? "table"
                        : node.data?.app === "Zapier Chatbots"
                          ? "chatbot"
                          : node.data?.app === "Zapier Agents"
                            ? "agent"
                            : node.data?.app === "Zapier Interfaces"
                              ? "interface"
                              : node.nodeType === "3rd_party" ||
                                  node.type === "3rd_party"
                                ? "3rd_party"
                                : "unknown";
                return nodeTypeMapped === nodeType;
              });

              // For 3rd party nodes, only count nodes in the same sub-column
              let nodesInSamePosition = existingNodesInColumn;
              if (nodeType === "3rd_party") {
                nodesInSamePosition = existingNodes.filter((node) => {
                  return (
                    (node.nodeType === "3rd_party" ||
                      node.type === "3rd_party" ||
                      (node.data?.app &&
                        !node.data.app.startsWith("Zapier"))) &&
                    node.position?.x === finalColumn
                  );
                });
              }

              const yPosition = 100 + nodesInSamePosition.length * 380;
              inferred.position = { x: finalColumn, y: yPosition };
            }

            // Set node type
            inferred.type =
              nodeType === "zap"
                ? "zap"
                : nodeType === "text"
                  ? "text"
                  : "default";

            // Infer data properties based on type
            if (!inferred.data) {
              inferred.data = {};
            }

            if (nodeType === "table") {
              inferred.data.app = "Zapier Tables";
              inferred.data.services = [
                {
                  currentImplementation: {
                    id:
                      tablesApp?.current_implementation_id ||
                      FALLBACK_IMPLEMENTATION_IDS.TABLES,
                  },
                },
              ];
              // Auto-generate tableId if not provided
              if (!inferred.data.tableId) {
                // const tableCount =
                //   existingNodes.filter((n) => n.data?.tableId).length + 1;
                // inferred.data.tableId = `_TABLE_${tableCount}`;
              }
            } else if (nodeType === "chatbot") {
              inferred.data.app = "Zapier Chatbots";
              inferred.data.services = [
                {
                  currentImplementation: {
                    id:
                      chatbotsApp?.current_implementation_id ||
                      FALLBACK_IMPLEMENTATION_IDS.CHATBOTS,
                  },
                },
              ];
              // Auto-generate chatbotId if not provided
              if (!inferred.data.chatbotId) {
                // const chatbotCount =
                //   existingNodes.filter((n) => n.data?.chatbotId).length + 1;
                // inferred.data.chatbotId = `_CHATBOT_${chatbotCount}`;
              }
            } else if (nodeType === "agent") {
              inferred.data.app = "Zapier Agents";
              inferred.data.services = [
                {
                  currentImplementation: {
                    id:
                      agentsApp?.current_implementation_id ||
                      FALLBACK_IMPLEMENTATION_IDS.AGENTS,
                  },
                },
              ];
              // Auto-generate agentId if not provided
              if (!inferred.data.agentId) {
                // TODO: Auto-generate agentId if needed
                // const agentCount = existingNodes.filter((n) => n.data?.agentId).length + 1;
                // inferred.data.agentId = `_AGENT_${agentCount}`;
              }
            } else if (nodeType === "interface" || nodeType === "form") {
              inferred.data.app = "Zapier Interfaces";
              inferred.data.services = [
                {
                  currentImplementation: {
                    id:
                      interfacesApp?.current_implementation_id ||
                      FALLBACK_IMPLEMENTATION_IDS.INTERFACES,
                  },
                },
              ];
              // Auto-generate projectId and pageId if not provided
              if (!inferred.data.projectId) {
                // inferred.data.projectId = "_INTERFACE_1";
              }
              if (!inferred.data.pageId) {
                // TODO: Auto-generate pageId if needed
                // const pageCount = existingNodes.filter((n) => n.data?.pageId).length + 1;
                // inferred.data.pageId = `_PAGE_${pageCount}`;
              }
            } else if (nodeType === "zap") {
              // Auto-generate zapId if not provided
              if (!inferred.data.zapId) {
                // TODO: Auto-generate zapId if needed
                // const zapCount = existingNodes.filter((n) => n.data?.zapId).length + 1;
                // inferred.data.zapId = `_ZAP_${zapCount}`;
              }
            }

            return inferred;
          };

          // Parse node and edge diffs
          let nodeDiff: z.infer<typeof nodeDiffSchema>;
          let edgeDiff: z.infer<typeof edgeDiffSchema>;

          try {
            // Validate the node diff against the schema (defaults fill in
            // omitted add/remove/modify arrays).
            nodeDiff = nodeDiffSchema.parse(args.nodeDiff ?? {});
          } catch (error) {
            console.error(error);
            // Surface the actual validation issues instead of "[object Object]".
            const details =
              error instanceof z.ZodError
                ? JSON.stringify(error.issues)
                : String(error);
            return {
              $error: {
                message: `Invalid nodeDiff format. Expected JSON diff with structure: { "add": [...], "remove": [...], "modify": [...] }. Validation errors: ${details}. Received: ${JSON.stringify(args.nodeDiff)}`,
              },
            };
          }

          try {
            // Validate the edge diff against the schema (defaults fill in
            // omitted add/remove arrays).
            edgeDiff = edgeDiffSchema.parse(args.edgeDiff ?? {});
          } catch (error) {
            console.error(error);
            const details =
              error instanceof z.ZodError
                ? JSON.stringify(error.issues)
                : String(error);
            return {
              $error: {
                message: `Invalid edgeDiff format. Expected JSON diff with structure: { "add": [...], "remove": [...] }. Validation errors: ${details}. Received: ${JSON.stringify(args.edgeDiff)}`,
              },
            };
          }

          // Apply the node diff to current nodes
          let newNodes = [...currentNodes];

          // Remove nodes
          if (nodeDiff.remove) {
            newNodes = newNodes.filter(
              (node) => !nodeDiff.remove.includes(node.id),
            );
          }

          // Track invalid node IDs and their valid replacements
          const invalidNodeIdMap = new Map<string, string>();

          // Check existing nodes for invalid UUIDs that might be referenced by edges
          newNodes.forEach((node) => {
            if (node.id && !isValidUUID(node.id)) {
              // Find another node with valid UUID that matches this node's data
              const validReplacement = newNodes.find((n) => {
                if (n.id === node.id) return false; // Skip self
                if (!isValidUUID(n.id)) return false; // Must be valid UUID

                // Try to match by asset ID or other identifying properties
                if (node.data?.tableId && n.data?.tableId === node.data.tableId)
                  return true;
                if (
                  node.data?.chatbotId &&
                  n.data?.chatbotId === node.data.chatbotId
                )
                  return true;
                if (node.data?.agentId && n.data?.agentId === node.data.agentId)
                  return true;
                if (node.data?.zapId && n.data?.zapId === node.data.zapId)
                  return true;
                if (
                  node.data?.projectId &&
                  node.data?.pageId &&
                  n.data?.projectId === node.data.projectId &&
                  n.data?.pageId === node.data.pageId
                )
                  return true;

                return false;
              });

              if (validReplacement) {
                invalidNodeIdMap.set(node.id, validReplacement.id);
              }
            }
          });

          // Modify nodes
          if (nodeDiff.modify.length > 0) {
            nodeDiff.modify.forEach((modifiedNode) => {
              const modifiedNodeData = modifiedNode.data;
              if (modifiedNode.id && !isValidUUID(modifiedNode.id)) {
                const existingNodeIndex = newNodes.findIndex((node) => {
                  // Try to find by various asset IDs
                  if (
                    modifiedNode.data?.tableId &&
                    node.data?.tableId === modifiedNode.data.tableId
                  )
                    return true;
                  if (
                    modifiedNode.data?.chatbotId &&
                    node.data?.chatbotId === modifiedNode.data.chatbotId
                  )
                    return true;
                  if (
                    modifiedNode.data?.agentId &&
                    node.data?.agentId === modifiedNode.data.agentId
                  )
                    return true;
                  if (
                    modifiedNode.data?.zapId &&
                    node.data?.zapId === modifiedNode.data.zapId
                  )
                    return true;
                  if (
                    modifiedNode.data?.projectId &&
                    modifiedNode.data?.pageId &&
                    node.data?.projectId === modifiedNode.data.projectId &&
                    node.data?.pageId === modifiedNode.data.pageId
                  )
                    return true;
                  return false;
                });

                if (
                  existingNodeIndex !== -1 &&
                  isValidUUID(newNodes[existingNodeIndex].id)
                ) {
                  invalidNodeIdMap.set(
                    modifiedNode.id,
                    newNodes[existingNodeIndex].id,
                  );
                }
              } else if (modifiedNode.id) {
                let nodeIndex = newNodes.findIndex(
                  (node) => node.id === modifiedNode.id,
                );

                // If not found by ID, try to find by asset ID (tableId, chatbotId, etc.)
                if (nodeIndex === -1) {
                  if (modifiedNodeData.tableId) {
                    nodeIndex = newNodes.findIndex(
                      (node) => node.data?.tableId === modifiedNodeData.tableId,
                    );
                  } else if (modifiedNodeData.chatbotId) {
                    nodeIndex = newNodes.findIndex(
                      (node) =>
                        node.data?.chatbotId === modifiedNodeData.chatbotId,
                    );
                  } else if (
                    modifiedNodeData.projectId &&
                    modifiedNodeData.pageId
                  ) {
                    nodeIndex = newNodes.findIndex(
                      (node) =>
                        node.data?.projectId === modifiedNodeData.projectId &&
                        node.data?.pageId === modifiedNodeData.pageId,
                    );
                  } else if (modifiedNodeData.zapId) {
                    nodeIndex = newNodes.findIndex(
                      (node) => node.data?.zapId === modifiedNodeData.zapId,
                    );
                  }
                }

                if (nodeIndex !== -1) {
                  // Keep the original node ID when replacing. Only overwrite
                  // with concrete values so an explicit null/undefined id (e.g.
                  // tableId: null from the gateway model) can't wipe an
                  // existing id.
                  newNodes[nodeIndex] = {
                    ...newNodes[nodeIndex],
                    data: mergeModifiedNodeData(
                      newNodes[nodeIndex].data,
                      modifiedNodeData,
                    ),
                    id: newNodes[nodeIndex].id,
                  };
                }
              }
            });
          }

          // Add nodes with inference
          if (nodeDiff.add) {
            // Filter out agent nodes first if toggle is off, then process remaining nodes
            const filteredNodesToAdd = nodeDiff.add.filter((node: any) => {
              // Allow node if agents are enabled OR if it's not an agent node
              return (
                isAgentsEnabled ||
                !(
                  (node.assetType && node.assetType === "agent") ||
                  node.type === "agent" ||
                  node.nodeType === "agent" ||
                  node.data?.app === "Zapier Agents" ||
                  node.data?.agentId
                )
              );
            });

            const addedNodes = filteredNodesToAdd.map(
              (node: any, index: number) => {
                // Track invalid UUIDs before replacing them
                if (node.id && !isValidUUID(node.id)) {
                  const newValidId = uuid();
                  invalidNodeIdMap.set(node.id, newValidId);
                  node.id = newValidId;
                } else if (!node.id) {
                  node.id = uuid();
                }

                // Determine node type from explicit type or infer from data
                let nodeType = node.nodeType || node.type;
                if (!nodeType) {
                  if (node.data?.tableId || node.data?.app === "Zapier Tables")
                    nodeType = "table";
                  else if (
                    node.data?.chatbotId ||
                    node.data?.app === "Zapier Chatbots"
                  )
                    nodeType = "chatbot";
                  else if (
                    node.data?.agentId ||
                    node.data?.app === "Zapier Agents"
                  )
                    nodeType = "agent";
                  else if (
                    node.data?.projectId ||
                    node.data?.app === "Zapier Interfaces"
                  )
                    nodeType = "interface";
                  else if (node.data?.zapId) nodeType = "zap";
                  else nodeType = "text";
                }

                // Apply inference with updated nodes list that includes previously added nodes
                const nodesForInference = [
                  ...newNodes,
                  ...nodeDiff.add.slice(0, index).map((prevNode: any) => ({
                    ...prevNode,
                    id: prevNode.id || uuid(),
                    type:
                      prevNode.nodeType === "zap"
                        ? "zap"
                        : prevNode.nodeType === "text"
                          ? "text"
                          : "default",
                    data: {
                      ...prevNode.data,
                      app:
                        prevNode.nodeType === "table"
                          ? "Zapier Tables"
                          : prevNode.nodeType === "chatbot"
                            ? "Zapier Chatbots"
                            : prevNode.nodeType === "agent"
                              ? "Zapier Agents"
                              : prevNode.nodeType === "interface" ||
                                  prevNode.nodeType === "form"
                                ? "Zapier Interfaces"
                                : prevNode.data?.app,
                    },
                  })),
                ];

                const inferredNode = inferNodeProperties(
                  node,
                  nodesForInference,
                );

                return inferredNode;
              },
            );
            newNodes = [...newNodes, ...addedNodes];
          }

          // Apply the edge diff to current edges
          let newEdges = [...currentEdges];

          // Remove edges
          if (edgeDiff.remove) {
            newEdges = newEdges.filter(
              (edge) => !edgeDiff.remove.includes(edge.id),
            );
          }

          // Fix existing edges that reference invalid node IDs
          newEdges = newEdges.map((edge) => {
            const updatedEdge = { ...edge };

            if (edge.source && invalidNodeIdMap.has(edge.source)) {
              updatedEdge.source = invalidNodeIdMap.get(edge.source)!;
            }

            if (edge.target && invalidNodeIdMap.has(edge.target)) {
              updatedEdge.target = invalidNodeIdMap.get(edge.target)!;
            }

            return updatedEdge;
          });

          // Add edges with default properties
          if (edgeDiff.add) {
            const addedEdges = edgeDiff.add.map((edge: any) => {
              // Generate unique ID if not provided
              if (!edge.id || !isValidUUID(edge.id)) {
                edge.id = uuid();
              }

              // Fix invalid source/target references using the mapping
              if (edge.source && invalidNodeIdMap.has(edge.source)) {
                edge.source = invalidNodeIdMap.get(edge.source);
              }

              if (edge.target && invalidNodeIdMap.has(edge.target)) {
                edge.target = invalidNodeIdMap.get(edge.target);
              }

              // Set default marker properties if not provided
              if (!edge.markerEnd) {
                edge.markerEnd = {
                  type: "arrow",
                  strokeWidth: 1.5,
                  color: "#413735",
                };
              }

              return edge;
            });

            newEdges = [...newEdges, ...addedEdges];
          }

          // Clean up bad props from nodes before setting
          const cleanedNodes = newNodes.map((node) => {
            const cleanedNode = { ...node } as any;

            // Remove nodeType property (doesn't exist in our schema)
            if (cleanedNode.nodeType) {
              delete cleanedNode.nodeType;
            }

            return cleanedNode;
          });

          // Update the nodes and edges store
          set(nodesAtom, cleanedNodes);
          set(edgesAtom, newEdges);

          try {
            const result = await set(saveCanvasSetter, {
              nodes: cleanedNodes,
              edges: newEdges,
            });

            if (!result?.success) {
              return {
                $error: {
                  message: `Failed to update canvas (likely an edges / nodes id issue)`,
                },
              };
            }
          } catch (error) {
            return {
              $error: {
                message: `Failed to save canvas: ${error instanceof Error ? error.message : "Unknown error"}`,
              },
            };
          }

          const nodeAddCount = nodeDiff.add?.length || 0;
          const nodeRemoveCount = nodeDiff.remove?.length || 0;
          const nodeModifyCount = nodeDiff.modify?.length || 0;
          const edgeAddCount = edgeDiff.add?.length || 0;
          const edgeRemoveCount = edgeDiff.remove?.length || 0;

          return {
            content: [
              {
                type: "text",
                text: `Canvas updated: ${nodeAddCount} nodes added, ${nodeRemoveCount} nodes removed, ${nodeModifyCount} nodes modified, ${edgeAddCount} edges added, ${edgeRemoveCount} edges removed`,
              },
            ],
          };
        } catch (error) {
          return {
            $error: {
              message: `Failed to update canvas: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          };
        }
      }),
    };
  },
);
editCanvasAtom.debugLabel = "editCanvasAtom";
